import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config';
import '../styles/components/profileTabs.css';

interface UserStats {
  user_id: number;
  user_email: string;
  item_count: number;
  photo_count: number;
  storage_bytes: number;
  db_storage_bytes: number;
}

interface SystemStats {
  total_users: number;
  total_items: number;
  total_photos: number;
  total_storage_bytes: number;
  total_db_storage_bytes: number;
  users: UserStats[];
}

const SystemStatsTab: React.FC = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      try {
        const response = await fetch(API_ENDPOINTS.GET_ALL_USERS_STATS, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch stats');
        const data = await response.json();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round(bytes / Math.pow(k, i))} ${sizes[i]}`;
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{error}</div>;
  if (!stats) return null;
  
  // Check if we have the expected data structure
  const hasUserStats = stats.users && Array.isArray(stats.users) && stats.users.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Summary Card */}
      <div style={{
        background: 'var(--color-bg-primary)',
        borderRadius: '12px',
        padding: '16px',
        margin: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px'
        }}>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-compact-value" style={{ color: 'var(--color-accent-secondary)' }}>{stats.total_users}</div>
            <div className="stat-compact-label">Users</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-compact-value" style={{ color: '#8b5cf6' }}>{stats.total_items}</div>
            <div className="stat-compact-label">Items</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-compact-value" style={{ color: '#ec4899' }}>{stats.total_photos}</div>
            <div className="stat-compact-label">Photos</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-compact-value-small" style={{ color: 'var(--color-success)' }}>{formatBytes(stats.total_storage_bytes)}</div>
            <div className="stat-compact-label">Photo Storage</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="stat-compact-value-small" style={{ color: 'var(--color-warning)' }}>{formatBytes(stats.total_db_storage_bytes)}</div>
            <div className="stat-compact-label">DB Storage</div>
          </div>
        </div>
      </div>

      {/* User Table Card */}
      {hasUserStats && (
      <div className="stats-table-scroll-container">
        <table className="stats-table-sticky">
          <thead>
            <tr>
              <th className="stats-table-sticky-col">User</th>
              <th>Items</th>
              <th>Photos</th>
              <th>Photo Storage</th>
              <th>DB Storage</th>
            </tr>
          </thead>
          <tbody>
            {stats.users.map((user: UserStats) => (
              <tr key={user.user_id}>
                <td
                  className={`stats-table-sticky-col${expandedUserId === user.user_id ? ' stats-table-sticky-col-expanded' : ''}`}
                  onClick={() => setExpandedUserId(expandedUserId === user.user_id ? null : user.user_id)}
                  title={user.user_email}
                >{user.user_email}</td>
                <td>{user.item_count}</td>
                <td>{user.photo_count}</td>
                <td>{formatBytes(user.storage_bytes)}</td>
                <td>{formatBytes(user.db_storage_bytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default SystemStatsTab;
